// SPDX-License-Identifier: MIT
/**
 * Data on an interface: tabs, progress, a ring for a rate, a stepper, bars and metric tiles. Values
 * are brand facts as written ("99.2%", "6,500"); charts plot only those, never an invented series.
 */
import { TAU, clamp } from '../../core/math.ts';
import { mix } from '../color.ts';
import { curves } from '../curves.ts';
import { parseStat, wrap } from '../text.ts';
import { label } from './controls.ts';
import { type Box, fitLabel, icon, panel, rrect } from './draw.ts';
import { odometer, odometerWidth } from './motion.ts';
import type { UiTokens } from './tokens.ts';
type Ctx = CanvasRenderingContext2D;
/** Segmented tabs; `active` may be fractional while the selection slides between two tabs. */
export function tabs(ctx: Ctx, tk: UiTokens, b: Box, labels: string[], active: number): void {
    const n = Math.max(1, labels.length);
    rrect(ctx, b, b.h / 2);
    ctx.fillStyle = tk.sunken;
    ctx.fill();
    const seg = (b.w - 8) / n;
    const a = clamp(active, 0, n - 1);
    const pill: Box = { x: b.x + 4 + a * seg, y: b.y + 4, w: seg, h: b.h - 8 };
    panel(ctx, tk, pill, { r: pill.h / 2, fill: tk.raised, lift: 0.6, border: !tk.dark });
    labels.forEach((t, i) => {
        const near = clamp(1 - Math.abs(i - a));
        fitLabel(ctx, tk, t, seg - 24, tk.type.caption, 600);
        label(ctx, t, b.x + 4 + seg * (i + 0.5), b.y + b.h / 2 + 1, mix(tk.muted, tk.text, near), 'center');
    });
}
/** A progress track filled to `value` (0–1). */
export function progress(ctx: Ctx, tk: UiTokens, b: Box, value: number): void {
    rrect(ctx, b, b.h / 2);
    ctx.fillStyle = tk.track;
    ctx.fill();
    const w = b.w * clamp(value);
    if (w <= 0)
        return;
    rrect(ctx, { ...b, w: Math.max(w, b.h) }, b.h / 2);
    ctx.fillStyle = tk.accent;
    ctx.fill();
}
/** A ring filled to `value` (0–1) of a full turn, starting at 12 o'clock. */
export function ring(ctx: Ctx, tk: UiTokens, cx: number, cy: number, r: number, value: number, width = r * 0.16): void {
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TAU);
    ctx.strokeStyle = tk.track;
    ctx.stroke();
    const v = clamp(value);
    if (v <= 0)
        return;
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + TAU * v);
    ctx.strokeStyle = tk.accent;
    ctx.stroke();
}
/**
 * `n` step markers along a line from (x, y) over `w`. `at` is the position along the steps (0 =
 * first step current, 1.5 = first done and half way to the third). Markers carry ordinals 1–n.
 */
export function stepper(ctx: Ctx, tk: UiTokens, x: number, y: number, w: number, n: number, at: number): void {
    const r = 24;
    const gap = n > 1 ? (w - 2 * r) / (n - 1) : 0;
    const cx = (i: number) => x + r + gap * i;
    for (let i = 0; i < n - 1; i++) {
        const x0 = cx(i) + r + 8;
        const x1 = cx(i + 1) - r - 8;
        ctx.fillStyle = tk.track;
        ctx.fillRect(x0, y - 2, x1 - x0, 4);
        const k = clamp(at - i);
        if (k > 0) {
            ctx.fillStyle = tk.accent;
            ctx.fillRect(x0, y - 2, (x1 - x0) * k, 4);
        }
    }
    for (let i = 0; i < n; i++) {
        const done = clamp((at - i) / 0.35);
        const current = clamp(1 - Math.abs(at - i) * 2);
        ctx.beginPath();
        ctx.arc(cx(i), y, r, 0, TAU);
        ctx.fillStyle = mix(tk.surface, tk.accent, done);
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = mix(tk.track, tk.accent, Math.max(done, current));
        ctx.stroke();
        if (done < 1) {
            ctx.font = tk.font(tk.type.caption, 600);
            const a = ctx.globalAlpha;
            ctx.globalAlpha = a * (1 - done);
            label(ctx, String(i + 1), cx(i), y + 1, mix(tk.muted, tk.accentText, current), 'center');
            ctx.globalAlpha = a;
        }
        if (done > 0)
            icon(ctx, 'check', cx(i) - 14, y - 14, 28, tk.onAccent, done);
    }
}
export type BarItem = {
    label: string;
    value: string;
};
/**
 * Bars for facts that share a unit, heights by value. Bars grow in turn as `grow` goes 0→1 and
 * their values roll in above them; `highlight` takes the accent, the others a tint of it.
 */
export function bars(ctx: Ctx, tk: UiTokens, b: Box, items: BarItem[], grow: number, highlight = -1): void {
    const values = items.map((it) => parseStat(it.value)?.n ?? 0);
    const max = Math.max(1e-9, ...values);
    const n = Math.max(1, items.length);
    const slot = b.w / n;
    const bw = Math.min(slot * 0.56, 180);
    const labelH = 48;
    const valueH = 52;
    const base = b.y + b.h - labelH;
    const full = b.h - labelH - valueH;
    ctx.fillStyle = tk.border;
    ctx.fillRect(b.x, base, b.w, 1.5);
    items.forEach((it, i) => {
        const k = curves.glide(clamp((grow - i * 0.12) / 0.64));
        const h = full * (values[i]! / max) * k;
        const x = b.x + slot * (i + 0.5) - bw / 2;
        if (h > 0.5) {
            ctx.beginPath();
            ctx.roundRect(x, base - h, bw, h, [Math.min(tk.radius.sm, h), Math.min(tk.radius.sm, h), 0, 0]);
            ctx.fillStyle = i === highlight ? tk.accent : mix(tk.accent, tk.surface, 0.55);
            ctx.fill();
        }
        ctx.font = tk.font(tk.type.title, 700);
        const vw = odometerWidth(ctx, it.value);
        ctx.fillStyle = tk.text;
        const a = ctx.globalAlpha;
        ctx.globalAlpha = a * clamp(k * 2);
        odometer(ctx, it.value, k, x + bw / 2 - vw / 2, base - h - 16);
        ctx.globalAlpha = a;
        fitLabel(ctx, tk, it.label, slot - 16, tk.type.caption, 500);
        label(ctx, it.label, x + bw / 2, base + labelH / 2 + 4, tk.muted, 'center');
    });
}
/** Largest value size (display down to heading) at which `value` fits a tile `b`. Give tiles in a row the smallest. */
export function kpiValuePx(ctx: Ctx, tk: UiTokens, b: Box, value: string): number {
    let px = Math.min(tk.type.display, Math.round(b.h * 0.5));
    ctx.font = tk.font(px, 700);
    while (px > tk.type.heading && odometerWidth(ctx, value) > b.w - 56)
        ctx.font = tk.font(--px, 700);
    return px;
}
/**
 * A metric tile: label on top (wrapping to a second line if it must), the fact's value rolling in
 * large below. `count` 0–1 drives the roll; `valuePx` defaults to the largest size that fits.
 */
export function kpi(ctx: Ctx, tk: UiTokens, b: Box, title: string, value: string, count: number, valuePx: number = kpiValuePx(ctx, tk, b, value)): void {
    panel(ctx, tk, b, { lift: 1 });
    const pad = 28;
    // The label wraps rather than shrinking below the caption size; it is a claim and is shown whole.
    ctx.font = tk.font(tk.type.caption, 500);
    wrap(ctx, title, b.w - 2 * pad).forEach((l, i) => label(ctx, l.text, b.x + pad, b.y + pad + 14 + i * 32, tk.muted));
    ctx.font = tk.font(valuePx, 700);
    ctx.fillStyle = tk.text;
    ctx.textBaseline = 'alphabetic';
    odometer(ctx, value, count, b.x + pad, b.y + b.h - pad);
}
